import { Container, Graphics, Text } from "pixi.js"

import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import { getUiMetrics } from "./ui_metrics"

// The frame-rate readout, toggled with `f`.
//
// It deliberately shows more than a frame rate, because a frame rate on its own is the one number
// that hides the problem we actually care about (see the budget-hardware target): a client that
// averages 58fps while dropping one 90ms frame every second reads as "fine" and feels awful. So the
// panel carries three things that disagree with each other usefully:
//
//   * the rate, averaged over the last second -- throughput, the number everyone asks for;
//   * avg / worst frame time in that same second -- the worst is the hitch, and it is the reading
//     that moves when something expensive (a bake, a full map rerender) lands in one frame;
//   * a bar per frame over that second, so a single spike is visibly a SPIKE and not a dip in an
//     average. Each bar is coloured against the 60fps budget, and the budget itself is drawn as a
//     hairline, so "over budget" is a thing you see rather than a number you compare.
//
// It measures the real loop: it is a stage child fed from the renderer's own frame delta, not a
// separate rAF. That also means it costs something, so it is careful to cost very little -- the
// numbers are re-rasterized a few times a second rather than every frame (see TEXT_REFRESH_MS), and
// nothing is drawn at all while it is hidden, which is almost always.

const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.72
const BORDER_COLOR = 0xffffff
const BORDER_ALPHA = 0.14
const CORNER = 5

// The frame budget everything is measured against. A bar at or under this is green; up to twice it
// is amber; beyond that it is red -- a frame that took 33ms+ dropped at least one whole refresh.
export const FRAME_BUDGET_MS = 1000 / 60
const GOOD_COLOR = 0x7be08a
const WARN_COLOR = 0xd9a441
const BAD_COLOR = 0xe8745a
const GUIDE_COLOR = 0xffffff
const GUIDE_ALPHA = 0.38

const RATE_COLOR = 0xffffff
const UNIT_COLOR = 0x8f9bb0
const DETAIL_COLOR = 0xcfd8dc

// How wide a window the numbers describe. One second is long enough that the rate is steady to read
// and short enough that a hitch shows up and then clears, rather than haunting the panel.
const WINDOW_MS = 1000
// A hard cap on the ring, so a machine running at 240Hz (or a pathological burst of zero-length
// frames) can't grow it without bound.
const MAX_SAMPLES = 240

// A "frame" longer than this is not a frame. It is the gap either side of something that stopped
// the loop entirely -- a backgrounded tab whose rAF was throttled to nothing, a page that has just
// finished loading, a laptop coming out of sleep. Letting one in parks a five-figure worst-frame on
// the panel for a full second every time the player tabs back, which buries the hitches that are
// real and fixable. Everything below it is kept, so a genuinely horrible 800ms frame still shows.
const STALL_CEILING_MS = 2000

// Frames are measured every frame; the TEXT is only re-rasterized this often. Re-styling a pixi Text
// rebuilds its texture, and doing that 60 times a second in the thing that reports frame cost would
// be measuring its own shadow. The bars still move every frame -- they are one Graphics redraw.
const TEXT_REFRESH_MS = 200

const PAD_X = 7
const PAD_Y = 5
const GRAPH_H = 16
const GRAPH_GAP = 5
const BAR_W = 2
const BAR_GAP = 1
const BAR_COUNT = 44
const GRAPH_W = BAR_COUNT * (BAR_W + BAR_GAP) - BAR_GAP
// The tallest frame time the graph can draw. It is three budgets rather than something generous,
// because the graph's job is to separate frames in the 16-50ms range -- the ones that are the
// difference between smooth and not. A taller ceiling would flatten all of those into the floor to
// make room for the rare frame nobody needs measured precisely: anything above this draws full
// height, which is what it means anyway, and its colour has already said so.
const GRAPH_CEILING_MS = FRAME_BUDGET_MS * 3

// How wide the panel should end up on the actual display, in CSS pixels.
//
// The HUD is laid out in a virtual 800-unit screen that is stretched to fit whatever is in front of
// the player, and on a phone that stretch runs the WRONG WAY: a portrait handset maps those 800
// units onto ~390 real pixels, so a panel that reads comfortably on a desktop lands at about 70px
// across with 4px digits -- present, correct, and completely unreadable. Every other touch control
// answers this by growing (chipMetrics, the control bar's 1.5x), so this one does too. Desktop is
// already past this width and is left exactly as it was.
const MIN_PANEL_CSS_W = 170
const MAX_PANEL_SCALE = 3

export interface FrameStats {
    // Frames per second over the window. 0 when nothing has been measured yet.
    fps: number
    // Mean frame time over the window, in ms.
    avgMs: number
    // The single worst frame in the window, in ms. The reading that catches hitches.
    worstMs: number
}

// What the panel says, from a window of frame durations. Pure, so the arithmetic is tested without
// a renderer. Non-finite and non-positive samples are dropped rather than averaged: a zero-length
// frame is a clock artifact, and letting one in sends the rate to infinity.
export const frameStats = (durationsMs: number[]): FrameStats => {
    let total = 0
    let count = 0
    let worst = 0
    for (const ms of durationsMs) {
        if (!isFinite(ms) || ms <= 0) {
            continue
        }
        total += ms
        count++
        if (ms > worst) {
            worst = ms
        }
    }
    if (!count) {
        return { fps: 0, avgMs: 0, worstMs: 0 }
    }
    return { fps: (count * 1000) / total, avgMs: total / count, worstMs: worst }
}

// A frame time, short enough to sit in the panel. Tenths up to 100ms (the resolution that matters
// when you are watching a 16.7ms budget), whole milliseconds past it, and a ceiling -- the panel is
// a fixed width and must not be pushed out of shape by one bad number.
export const formatMs = (ms: number): string => {
    if (!isFinite(ms) || ms < 0) {
        return "--"
    }
    if (ms >= 999.5) {
        return "999+"
    }
    return ms >= 100 ? ms.toFixed(0) : ms.toFixed(1)
}

// How much to magnify the panel so it ends up at least MIN_PANEL_CSS_W across on the display.
// `cssPerUnit` is CSS pixels per virtual HUD unit (UiMetrics.scaleX). Never shrinks: a display
// roomy enough to show the panel at its drawn size gets it at its drawn size.
export const panelScale = (panelWidth: number, cssPerUnit: number): number => {
    if (!(panelWidth > 0) || !(cssPerUnit > 0)) {
        return 1
    }
    return Math.min(MAX_PANEL_SCALE, Math.max(1, MIN_PANEL_CSS_W / (panelWidth * cssPerUnit)))
}

// Green inside the budget, amber for a frame that ran long, red for one that cost a whole refresh
// or more. Used for the bars and for the rate itself, so the headline number and the graph can
// never tell different stories.
export const budgetColor = (ms: number): number => {
    if (ms <= FRAME_BUDGET_MS * 1.05) {
        return GOOD_COLOR
    }
    return ms <= FRAME_BUDGET_MS * 2 ? WARN_COLOR : BAD_COLOR
}

export class FpsMeter extends Container {
    private plate = new Graphics()
    private graph = new Graphics()
    private rate: Text
    private unit: Text
    private detail: Text

    // Frame durations over roughly the last second, oldest first.
    private samples: number[] = []
    private sinceTextMs = TEXT_REFRESH_MS

    private panelW: number
    private panelH: number
    // The display scale the panel was last sized for, so a resize is noticed without asking every
    // frame what it would be.
    private scaledFor = 0

    constructor() {
        super()

        this.rate = new TextRenderer().role("compactTitle").color(RATE_COLOR).strokeThickness(0).render("--") as Text
        this.unit = new TextRenderer().role("overline").color(UNIT_COLOR).strokeThickness(0).render("FPS") as Text
        this.detail = new TextRenderer().role("overline").color(DETAIL_COLOR).strokeThickness(0).render("") as Text

        this.panelW = PAD_X * 2 + GRAPH_W
        this.panelH = PAD_Y * 2 + this.rate.height + GRAPH_GAP + GRAPH_H

        this.addChild(this.plate, this.rate, this.unit, this.detail, this.graph)
        this.layout()
        this.drawPlate()
        this.fitToDisplay()

        // Starts away: this is a tool, not a HUD element, and the player who never presses `f`
        // should never pay for it.
        this.visible = false
    }

    private layout = () => {
        this.rate.x = PAD_X
        this.rate.y = PAD_Y
        this.unit.x = PAD_X + this.rate.width + 3
        // Sit the unit on the number's baseline rather than its top, so "FPS" reads as a suffix.
        this.unit.y = PAD_Y + this.rate.height - this.unit.height - 1
        this.detail.y = this.unit.y
        this.graph.x = PAD_X
        this.graph.y = this.panelH - PAD_Y - GRAPH_H
    }

    private drawPlate = () => {
        this.plate
            .clear()
            .beginFill(PANEL_COLOR, PANEL_ALPHA)
            .lineStyle({ width: 1, color: BORDER_COLOR, alpha: BORDER_ALPHA, alignment: 0 })
            .drawRoundedRect(0, 0, this.panelW, this.panelH, CORNER)
            .endFill()
    }

    /** The plate's drawn size, for whoever is placing it. Not `width`/`height`, which pixi derives
     * from bounds and which collapse to nothing while the panel is hidden. */
    panelSize = (): { width: number; height: number } => ({ width: this.panelW, height: this.panelH })

    /**
     * Magnify the panel on a display that would otherwise draw it too small to read -- which in
     * practice means a phone. Cheap to call: it compares one number and returns.
     */
    fitToDisplay = () => {
        const cssPerUnit = getUiMetrics().scaleX
        if (cssPerUnit === this.scaledFor) {
            return
        }
        this.scaledFor = cssPerUnit
        this.scale.set(panelScale(this.panelW, cssPerUnit))
    }

    /** The frame durations currently being reported on, oldest first. For tests. */
    frameWindow = (): number[] => [...this.samples]

    /** Is the readout up? */
    isOpen = (): boolean => this.visible

    /**
     * Show or hide the readout. Hiding drops the window, so the panel doesn't come back up
     * reporting the frame times of whenever it was last looked at -- including the enormous one
     * that is the gap between then and now.
     */
    setOpen = (open: boolean) => {
        if (open === this.visible) {
            return
        }
        this.visible = open
        if (!open) {
            this.samples = []
        }
        this.sinceTextMs = TEXT_REFRESH_MS
    }

    toggle = (): boolean => {
        this.setOpen(!this.visible)
        return this.visible
    }

    /**
     * One frame's delta, in ms, from the render loop. A no-op while hidden -- including the
     * sampling, so the panel measures the frames it is actually drawn into.
     */
    sample = (deltaMs: number) => {
        if (!this.visible) {
            return
        }
        // A phone can be turned over while the panel is up, and the virtual screen is reshaped when
        // it is (see canvas_layout).
        this.fitToDisplay()
        if (isFinite(deltaMs) && deltaMs > 0 && deltaMs < STALL_CEILING_MS) {
            this.samples.push(deltaMs)
        }
        // Trim to the window from the front: drop the oldest until what's left spans WINDOW_MS.
        let total = 0
        for (const ms of this.samples) {
            total += ms
        }
        while (this.samples.length > 1 && (total - this.samples[0] >= WINDOW_MS || this.samples.length > MAX_SAMPLES)) {
            total -= this.samples.shift()
        }

        this.drawGraph()

        this.sinceTextMs += deltaMs
        if (this.sinceTextMs >= TEXT_REFRESH_MS) {
            this.sinceTextMs = 0
            this.drawNumbers()
        }
    }

    private drawNumbers = () => {
        const { fps, avgMs, worstMs } = frameStats(this.samples)
        if (!fps) {
            return
        }
        this.rate.text = String(Math.round(fps))
        this.rate.tint = budgetColor(avgMs)
        this.detail.text = `${formatMs(avgMs)} / ${formatMs(worstMs)} ms`
        // The number's width moves as it crosses 9/99, and the detail is right-aligned against the
        // panel edge, so both followers are re-placed whenever the text changes.
        this.unit.x = PAD_X + this.rate.width + 3
        this.detail.x = this.panelW - PAD_X - this.detail.width
    }

    // The last BAR_COUNT frames, newest at the right, each scaled against GRAPH_CEILING_MS. One
    // Graphics redraw a frame: ~40 rects, which is cheap enough to not show up in its own graph.
    private drawGraph = () => {
        const g = this.graph.clear()
        const shown = this.samples.slice(-BAR_COUNT)
        const left = GRAPH_W - shown.length * (BAR_W + BAR_GAP) + BAR_GAP

        g.beginFill(BORDER_COLOR, 0.05).drawRect(0, 0, GRAPH_W, GRAPH_H).endFill()

        shown.forEach((ms, i) => {
            const h = Math.max(1, Math.min(1, ms / GRAPH_CEILING_MS) * GRAPH_H)
            g.beginFill(budgetColor(ms), 0.95)
                .drawRect(left + i * (BAR_W + BAR_GAP), GRAPH_H - h, BAR_W, h)
                .endFill()
        })

        // The budget, last and across the whole width, so it reads OVER the bars rather than being
        // hidden by the very bars it exists to be compared against. A frame that made 60fps stops
        // just under it; one that didn't pokes through it.
        //
        // Drawn one pixel clear of the height a 16.7ms frame actually reaches. Sitting it exactly
        // there is the truthful placement and an invisible one: the line and the tops of every
        // green bar are then the same row of pixels, and the guide disappears into the thing it is
        // supposed to be a guide for.
        const budgetY = Math.round(GRAPH_H - (FRAME_BUDGET_MS / GRAPH_CEILING_MS) * GRAPH_H) - 2
        g.beginFill(GUIDE_COLOR, GUIDE_ALPHA).drawRect(0, Math.max(0, budgetY), GRAPH_W, 1).endFill()
    }
}
